import { z } from 'zod';
import { VALID_SDR_STATUSES, SDR_STATUS } from '../constants/sdr-status.js';

/**
 * Phone number validation
 * Accepts E.164 format (e.g., +1234567890) or flexible international format
 */
const phoneSchema = z.string().refine(
  (val) => {
    if (!val) return true; // Allow null/empty (will be handled by nullable())

    // E.164 format: + followed by 10-15 digits
    if (/^\+[1-9]\d{6,14}$/.test(val)) return true;

    // Flexible international format: +, optional spaces, dashes, parentheses
    // e.g., +1 (555) 123-4567, +44 20 7123 4567
    if (/^\+[\d\s\-\(\)]{8,20}$/.test(val)) return true;

    return false;
  },
  { message: 'Invalid phone number format. Use E.164 format (e.g., +1234567890) or international format with country code (e.g., +1 555-123-4567)' }
);

/**
 * Update lead request schema
 * Only workflow fields are allowed
 */
export const updateLeadSchema = z.object({
  im_comment: z.string().optional().nullable(),
  // Fix: Use z.string().refine() instead of z.enum() for dynamic enum values
  // This ensures runtime validation works correctly with Object.values()
  sdr_status: z.union([
    z.string().refine(
      (val) => VALID_SDR_STATUSES.includes(val as any),
      { message: `sdr_status must be one of: ${VALID_SDR_STATUSES.join(', ')}` }
    ),
    z.null(),
    z.undefined(),
  ]).optional(),
  sdr_last_contacted_at: z.string().datetime().optional().nullable(),
  sdr_comment: z.string().optional().nullable(),
  // Call feedback field (for Appointment Booked leads)
  call_feedback: z.union([
    z.string().refine(
      (val) => ['NO-SHOW', 'RESCHEDULED', 'QUALIFIED', 'UNQUALIFIED', 'ASSUME_QUALIFIED', 'CANCELLED_NOT_A_FIT', 'CANCELLED_BY_CUSTOMER'].includes(val),
      { message: 'call_feedback must be one of: NO-SHOW, RESCHEDULED, QUALIFIED, UNQUALIFIED, ASSUME_QUALIFIED, CANCELLED_NOT_A_FIT, CANCELLED_BY_CUSTOMER' }
    ),
    z.null(),
    z.undefined(),
  ]).optional(),
  // Meeting scheduling fields (for Appointment Booked leads)
  // Accept date-only format (YYYY-MM-DD) - backend will convert to datetime
  // Industry best practice: Use union to explicitly handle null/undefined, then refine string
  meeting_date: z.union([
    z.string().refine(
      (val) => {
        // Accept YYYY-MM-DD format (date-only) - primary format
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return true;
        // Also accept ISO 8601 datetime format (for backward compatibility)
        if (/^\d{4}-\d{2}-\d{2}T[\d:\.]+(Z|[+-]\d{2}:\d{2})?$/.test(val)) return true;
        return false;
      },
      { message: 'meeting_date must be in YYYY-MM-DD or ISO 8601 datetime format' }
    ),
    z.null(),
    z.undefined(),
  ]).optional(),
  meeting_time: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/).optional().nullable(), // HH:mm format
  meeting_timezone: z.string().optional().nullable(), // IANA timezone identifier
  // Contact overrides (editable by all authenticated users; override takes precedence over webhook value)
  phone_override: phoneSchema.optional().nullable(),
  to_name_override: z.string().optional().nullable(),
  company_name_override: z.string().optional().nullable(),
  website_override: z.string().optional().nullable(),
});

export type UpdateLeadRequest = z.infer<typeof updateLeadSchema>;

/**
 * Create note request schema
 */
export const createNoteSchema = z.object({
  note_text: z.string().min(1, 'Note text is required'),
});

export type CreateNoteRequest = z.infer<typeof createNoteSchema>;

/**
 * Create lead request schema
 * Used for manually adding leads to the system (e.g., from Appointment Booked page)
 */
export const createLeadSchema = z.object({
  // MANDATORY FIELDS
  to_email: z.string().email('Invalid email address'),
  client_code: z.string().min(1, 'Client code is required'),
  explanation_note: z.string().min(10, 'Explanation note must be at least 10 characters'),

  // OPTIONAL FIELDS
  to_name: z.string().optional(),
  lead_id: z.number().int().positive().optional(),
  campaign_name: z.string().optional(),
  phone_number: z.string().optional(),
  website: z.string().optional(),
  company_name: z.string().optional(),

  // Meeting scheduling (optional)
  meeting_date: z.string().datetime().optional(),
  meeting_time: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/).optional(),
  meeting_timezone: z.string().optional(),

  // Workflow fields (optional)
  sdr_status: z.string().refine(
    (val) => val === undefined || VALID_SDR_STATUSES.includes(val as any),
    { message: `sdr_status must be one of: ${VALID_SDR_STATUSES.join(', ')}` }
  ).optional(),
  sdr_last_contacted_at: z.string().datetime().optional(),
  call_feedback: z.string().optional(),
  im_comment: z.string().optional(),
  sdr_comment: z.string().optional(),
});

export type CreateLeadRequest = z.infer<typeof createLeadSchema>;

/**
 * Leads list query parameters schema
 */
export const leadsListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  client_code: z.string().optional(),
  campaign_id: z.coerce.number().int().optional(),
  assigned_sdr_id: z.string().uuid().optional(),
  assigned_inbox_manager_id: z.string().uuid().optional(),
  assigned_account_manager_id: z.string().uuid().optional(),
  // Fix: Use z.string().refine() instead of z.enum() for dynamic enum values
  sdr_status: z.string().refine(
    (val) => val === undefined || VALID_SDR_STATUSES.includes(val as any),
    { message: `sdr_status must be one of: ${VALID_SDR_STATUSES.join(', ')}` }
  ).optional(),
  call_feedback: z.string().optional(), // Filter by call feedback (for Appointment Booked page)
  current_category_name: z.union([
    z.string(), // Single category: "Interested" (backward compatible)
    z.array(z.string()) // Multiple categories: ["Interested", "Appointment Booked"] (multi-select)
  ]).optional(),
  current_category_in: z.union([
    z.string(), // Single category: "Interested"
    z.array(z.string()) // Multiple categories: ["Interested", "Automated Followups Begun"]
  ]).optional(), // Filter by current category name IN array (preferred method for category filtering)
  date_from: z.string().datetime().optional(),
  date_to: z.string().datetime().optional(),
  meeting_date_from: z.string().datetime().optional(), // Filter by meeting date (appointment date)
  meeting_date_to: z.string().datetime().optional(), // Filter by meeting date (appointment date)
  sort: z.string().default('last_interested_at:desc'),
  category: z.string().optional(), // Filter by current_category_name (e.g., 'Interested', 'Appointment Booked')
  has_transitioned_to: z.string().optional(), // Filter leads that transitioned TO this category (cross-category filter)
});

export type LeadsListQuery = z.infer<typeof leadsListQuerySchema>;